import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Compass, GraduationCap, HeartPulse, Rocket } from "lucide-react";
import { Eyebrow, MaskLine, Reveal, SectionHeading } from "@/components/Reveal";
import ApplicationModal from "@/components/ApplicationModal";

import { API } from "@/lib/api";

const TEAM_IMG = "https://images.pexels.com/photos/1181738/pexels-photo-1181738.jpeg";

const CULTURE = [
  { Icon: Rocket, title: "Ownership", text: "Small teams, real responsibility. What you build ships and matters." },
  { Icon: GraduationCap, title: "Learning", text: "We invest in curiosity — new tools, new ideas and room to experiment." },
  { Icon: Compass, title: "Purpose", text: "Work on products that solve genuine, everyday problems for people and businesses." },
  { Icon: HeartPulse, title: "Balance", text: "Sustainable pace, honest communication and respect for life outside work." },
];

const Careers = () => {
  const [applyJob, setApplyJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/jobs`)
      .then((r) => setJobs(r.data))
      .catch(() => setJobs([]))
      .finally(() => setJobsLoaded(true));
  }, []);

  return (
  <>
    <section data-testid="careers-hero" className="cta-glow pb-24 pt-44 sm:pb-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Eyebrow className="text-gold">Careers</Eyebrow>
        <h1 className="mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <MaskLine delay={0.15}>Build the future of local</MaskLine>
          <MaskLine delay={0.3}>commerce with us.</MaskLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
            Join Sunkev Enterprises Private Limited and help build Craftsi and the next
            generation of digital products for businesses and customers.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <img
            src={TEAM_IMG}
            alt="Team collaborating at Sunkev Enterprises"
            className="h-[420px] w-full rounded-3xl object-cover shadow-2xl shadow-navy/15"
            loading="lazy"
          />
        </Reveal>
        <div>
          <SectionHeading
            eyebrow="Life at Sunkev"
            title="A place to do meaningful work."
            text="We're a technology company at an exciting stage — building platforms used by real businesses and customers. That means high ownership, fast learning and work you can point to."
          />
        </div>
      </div>
      <div className="mx-auto mt-16 grid max-w-7xl gap-6 px-5 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
        {CULTURE.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <div className="group h-full rounded-3xl border border-gray-100 bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-xl hover:shadow-navy/10">
              <c.Icon className="h-8 w-8 text-navy transition-colors duration-500 group-hover:text-gold" strokeWidth={1.5} />
              <h3 className="mt-6 font-heading text-xl font-bold text-ink">{c.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-fog">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="bg-mist py-24 sm:py-32">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Open Positions"
          title="Join the team building Craftsi."
          text="We're hiring for the people who will build and grow our flagship local services marketplace. All roles are based in Tirupati, Andhra Pradesh, with flexible hybrid options."
        />
        <div className="mt-14 space-y-5" data-testid="job-listings">
          {jobs.map((j, i) => (
            <Reveal key={j.title} delay={i * 0.06}>
              <article
                data-testid={`job-card-${i}`}
                className="group flex flex-col gap-6 rounded-3xl border border-gray-100 bg-white p-8 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-gold/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gold-dark">
                      {j.dept}
                    </span>
                    <span className="rounded-full bg-mist px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-fog">
                      {j.type}
                    </span>
                    <span className="rounded-full bg-mist px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-fog">
                      {j.location}
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading text-xl font-bold text-ink sm:text-2xl">
                    {j.title}
                  </h3>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-fog">{j.text}</p>
                </div>
                <button
                  onClick={() => setApplyJob(j.title)}
                  data-testid={`job-apply-${i}`}
                  className="group/btn inline-flex shrink-0 items-center gap-2 rounded-full bg-navy px-7 py-3.5 font-heading text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-lg hover:shadow-navy/25"
                >
                  Apply Now
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                </button>
              </article>
            </Reveal>
          ))}
        </div>
        {jobsLoaded && jobs.length === 0 && (
          <div
            data-testid="no-jobs"
            className="mt-14 rounded-3xl border border-gray-100 bg-white py-16 text-center"
          >
            <p className="font-heading text-lg font-bold text-ink">No open roles right now</p>
            <p className="mt-2 text-sm text-fog">New positions will be posted here as they open up.</p>
          </div>
        )}
        <Reveal delay={0.2}>
          <p className="mt-10 text-center text-sm text-fog">
            Don't see your role?{" "}
            <Link to="/contact?subject=Careers" className="font-semibold text-royal hover:text-navy">
              Introduce yourself
            </Link>{" "}
            — we keep strong profiles in mind as new roles open up.
          </p>
        </Reveal>
      </div>
    </section>

    {applyJob && <ApplicationModal role={applyJob} onClose={() => setApplyJob(null)} />}
  </>
  );
};

export default Careers;
